/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import verificationService from '../../services/verificationService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import {
  FileText,
  Building,
  MapPin,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ListChecks,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import { useToastContext } from '../../contexts/ToastContext';
import { useServiceCategories } from '../../services/serviceCategoryService';
import { useCountries } from '../../services/countryService';
import Step1BusinessInfo from '../../components/vendor/verification/Step1BusinessInfo';
import Step2ContactLocation from '../../components/vendor/verification/Step2ContactLocation';
import Step3Documents from '../../components/vendor/verification/Step3Documents';
import Step4Review from '../../components/vendor/verification/Step4Review';
import AddPlatformModal from '../../components/vendor/verification/AddPlatformModal';
import { VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorVerification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending, isLoading, refreshVerificationStatus } =
    useVendorVerification();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error: showError, info } = useToastContext();
  const hasNavigatedRef = useRef(false);
  const justSubmittedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && isVendorVerified && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      success('You have already completed verification!', 'success');
      navigate('/vendor');
    }
  }, [isLoading, isVendorVerified, navigate, success]);

  useEffect(() => {
    if (!isLoading && isVerificationPending && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;

      if (!justSubmittedRef.current) {
        info(
          'Verification Pending',
          'Your verification is currently pending review. Please wait for admin approval.'
        );
      }

      navigate('/vendor');
    }
  }, [isLoading, isVerificationPending, navigate, info]);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useServiceCategories({ active: true });

  const serviceCategories = categoriesData?.data?.data || [];

  const {
    data: countriesData,
    isLoading: countriesLoading,
    error: countriesError,
  } = useCountries({ active: true });

  const countries = countriesData?.data || [];
  const [formData, setFormData] = useState({
    business_name: '',
    service_categories: null,
    business_description: '',
    years_experience: '',
    specialties: [],
    business_phone: '',
    business_email: '',
    website: '',
    social_media: {},
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    business_license: null,
    insurance_certificate: null,
    id_document: null,
    insurance_info: '',
    business_hours: {
      monday: { available: true },
      tuesday: { available: true },
      wednesday: { available: true },
      thursday: { available: true },
      friday: { available: true },
      saturday: { available: true },
      sunday: { available: true },
    },
    team_size: '',
    certifications: '',
    references: '',
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');

  const steps = [
    { id: 1, title: 'Business', description: 'Who you are', icon: Building },
    { id: 2, title: 'Contact', description: 'How clients reach you', icon: MapPin },
    { id: 3, title: 'Documents', description: 'Proof & details', icon: FileText },
    { id: 4, title: 'Review', description: 'Confirm & submit', icon: CheckCircle },
  ];

  const progressPct = Math.round(((currentStep - 1) / (steps.length - 1)) * 100);

  useEffect(() => {
    if (user) {
      setFormData((prev) => {
        const updated = { ...prev };

        if (user.vendor_profile) {
          Object.assign(updated, user.vendor_profile);
          updated.specialties = user.vendor_profile.specialties || [];
        }

        if (!updated.business_email && user.email) {
          updated.business_email = user.email;
        }
        if (!updated.business_phone && user.phone) {
          updated.business_phone = user.phone;
        }
        if (!updated.city && user.city) {
          updated.city = user.city;
        }
        if (!updated.state && user.state) {
          updated.state = user.state;
        }
        if (!updated.address && user.address) {
          updated.address = user.address;
        }
        if (!updated.zip_code && user.zip_code) {
          updated.zip_code = user.zip_code;
        }

        return updated;
      });
    }
  }, [user]);

  useEffect(() => {
    if (user?.country_code) {
      setFormData((prev) => ({
        ...prev,
        country: user.country_code,
      }));
    }
  }, [user?.country_code]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleSpecialtyToggle = (specialty) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s) => s !== specialty)
        : [...prev.specialties, specialty],
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value,
      },
    }));
  };

  const addSocialMediaPlatform = () => {
    setNewPlatformName('');
    setShowAddPlatformModal(true);
  };

  const handleAddPlatform = () => {
    if (newPlatformName && newPlatformName.trim()) {
      const normalizedPlatform = newPlatformName.toLowerCase().trim();
      if (!formData.social_media[normalizedPlatform]) {
        handleSocialMediaChange(normalizedPlatform, '');
        setShowAddPlatformModal(false);
        setNewPlatformName('');
      } else {
        showError('This platform already exists!');
      }
    }
  };

  const handleCancelAddPlatform = () => {
    setShowAddPlatformModal(false);
    setNewPlatformName('');
  };

  const removeSocialMediaPlatform = (platform) => {
    setFormData((prev) => {
      const newSocialMedia = { ...prev.social_media };
      delete newSocialMedia[platform];
      return {
        ...prev,
        social_media: newSocialMedia,
      };
    });
  };

  const toggleDayAvailability = (day) => {
    setFormData((prev) => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          available: !prev.business_hours[day].available,
        },
      },
    }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Please upload a valid image (JPEG, PNG) or PDF file',
      }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'File size must be less than 10MB',
      }));
      return;
    }

    setUploadProgress((prev) => ({ ...prev, [field]: 0 }));

    try {
      const uploadResult = await verificationService.uploadDocument(file, field, (progress) => {
        setUploadProgress((prev) => ({ ...prev, [field]: progress }));
      });

      setFormData((prev) => ({
        ...prev,
        [field]: {
          file: file,
          name: file.name,
          url: uploadResult.data.url,
          filename: uploadResult.data.filename,
          size: uploadResult.data.size,
          mime_type: uploadResult.data.mime_type,
        },
      }));

      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));

      setUploadProgress((prev) => ({ ...prev, [field]: null }));
    } catch (error) {
      console.error('File upload error:', error);
      setErrors((prev) => ({
        ...prev,
        [field]: error.response?.data?.message || 'Failed to upload file. Please try again.',
      }));
      setUploadProgress((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
        if (!formData.service_categories)
          newErrors.service_categories = 'Please select a service category';
        if (!formData.business_description.trim())
          newErrors.business_description = 'Business description is required';
        if (formData.business_description.length > 500)
          newErrors.business_description = 'Business description must not exceed 500 characters';
        if (!formData.years_experience)
          newErrors.years_experience = 'Years of experience is required';
        if (formData.specialties.length === 0)
          newErrors.specialties = 'Please select at least one specialty';
        break;
      case 2:
        if (!formData.business_phone.trim()) newErrors.business_phone = 'Business phone is required';
        if (!formData.business_email.trim()) newErrors.business_email = 'Business email is required';
        if (!formData.address.trim()) newErrors.address = 'Business address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
        break;
      case 3:
        if (!formData.business_license?.url) {
          newErrors.business_license = 'Business license is required';
        }
        if (!formData.insurance_certificate?.url) {
          newErrors.insurance_certificate = 'Insurance certificate is required';
        }
        if (!formData.id_document?.url) {
          newErrors.id_document = 'ID document is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    justSubmittedRef.current = true;
    hasNavigatedRef.current = false;

    try {
      const verificationData = {
        user_id: user.id,
        verification_type: 'vendor_business',
        verification_status: 'pending',
        verification_data: {
          business_name: formData.business_name?.trim(),
          service_categories: formData.service_categories ? [formData.service_categories] : [],
          business_description: formData.business_description?.trim(),
          years_experience: formData.years_experience,
          specialties: formData.specialties || [],
          business_phone: formData.business_phone?.trim(),
          business_email: formData.business_email?.trim(),
          website: formData.website?.trim(),
          social_media: formData.social_media,
          address: formData.address?.trim(),
          city: formData.city?.trim(),
          state: formData.state?.trim(),
          zip_code: formData.zip_code?.trim(),
          country: formData.country,
          insurance_info: formData.insurance_info?.trim(),
          business_hours: formData.business_hours,
          team_size: formData.team_size?.trim(),
          certifications: formData.certifications?.trim(),
          references: formData.references?.trim(),
        },
        verification_document_url: {
          business_license: formData.business_license?.url || null,
          insurance_certificate: formData.insurance_certificate?.url || null,
          id_document: formData.id_document?.url || null,
        },
      };

      const vendorProfileData = {
        business_name: formData.business_name?.trim(),
        description: formData.business_description?.trim(),
        address: formData.address?.trim(),
        city: formData.city?.trim(),
        state: formData.state?.trim(),
        zip_code: formData.zip_code?.trim(),
        country: formData.country,
        website: formData.website?.trim(),
        social_media: formData.social_media,
        years_experience: parseInt(formData.years_experience) || 0,
        specialties: formData.specialties || [],
        insurance_info: formData.insurance_info?.trim(),
        business_hours: formData.business_hours,
        team_size: parseInt(formData.team_size) || null,
        certifications: formData.certifications?.trim() || null,
        references: formData.references?.trim() || null,
        service_categories: formData.service_categories ? [formData.service_categories] : [],
        is_verified: true,
      };

      await verificationService.submitVerificationWithProfile({
        verification_data: verificationData.verification_data,
        verification_document_url: verificationData.verification_document_url,
        vendor_profile_data: vendorProfileData,
      });

      success('Verification completed successfully! Your vendor account has been verified.');

      await refreshVerificationStatus();

      setTimeout(() => {
        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          navigate('/vendor');
        }
        justSubmittedRef.current = false;
      }, 300);
    } catch (error) {
      console.error('Verification submission error:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to submit verification. Please try again.';
      showError(errorMessage);
      justSubmittedRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const StatusCard = ({ icon: Icon, iconClass, title, body, footer }) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl shadow-gray-900/5 p-8 text-center">
        <div
          className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border ${iconClass}`}
        >
          <Icon className="h-7 w-7" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-2">
          {title}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">{body}</p>
        {footer ? (
          <p className="text-xs text-gray-500 dark:text-gray-500">{footer}</p>
        ) : null}
      </div>
    </div>
  );

  const VerificationSkeleton = () => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 animate-pulse space-y-6">
        <div className="h-9 w-40 rounded-xl bg-gray-200 dark:bg-gray-800" />
        <div className="space-y-3">
          <div className="h-8 w-56 rounded-lg bg-gray-200 dark:bg-gray-800" />
          <div className="h-4 w-80 max-w-full rounded bg-gray-200 dark:bg-gray-800" />
        </div>
        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-800" />
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 space-y-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    if (currentStep === 1 && categoriesLoading) {
      return (
        <div className="space-y-5 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      );
    }

    if (currentStep === 2 && countriesLoading) {
      return (
        <div className="space-y-5 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <Step1BusinessInfo
            formData={formData}
            errors={errors}
            serviceCategories={serviceCategories}
            categoriesLoading={categoriesLoading}
            categoriesError={categoriesError}
            handleInputChange={handleInputChange}
            handleSpecialtyToggle={handleSpecialtyToggle}
          />
        );
      case 2:
        return (
          <Step2ContactLocation
            formData={formData}
            errors={errors}
            countries={countries}
            countriesLoading={countriesLoading}
            countriesError={countriesError}
            handleInputChange={handleInputChange}
            handleSocialMediaChange={handleSocialMediaChange}
            removeSocialMediaPlatform={removeSocialMediaPlatform}
            addSocialMediaPlatform={addSocialMediaPlatform}
            showAddPlatformModal={showAddPlatformModal}
            setShowAddPlatformModal={setShowAddPlatformModal}
            newPlatformName={newPlatformName}
            setNewPlatformName={setNewPlatformName}
            handleAddPlatform={handleAddPlatform}
            handleCancelAddPlatform={handleCancelAddPlatform}
          />
        );
      case 3:
        return (
          <Step3Documents
            formData={formData}
            errors={errors}
            uploadProgress={uploadProgress}
            handleFileUpload={handleFileUpload}
            handleInputChange={handleInputChange}
            toggleDayAvailability={toggleDayAvailability}
          />
        );
      case 4:
        return (
          <Step4Review
            formData={formData}
            serviceCategories={serviceCategories}
            countries={countries}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return <VerificationSkeleton />;
  }

  if (isVendorVerified) {
    return (
      <StatusCard
        icon={ShieldCheck}
        iconClass="border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400"
        title="Already verified"
        body="You have already completed the verification process."
        footer="Redirecting to dashboard…"
      />
    );
  }

  if (isVerificationPending) {
    return (
      <StatusCard
        icon={Clock}
        iconClass="border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
        title="Verification pending"
        body="Your verification is under review. You’ll get an email once it’s complete."
        footer="Redirecting to dashboard…"
      />
    );
  }

  const activeStep = steps.find((s) => s.id === currentStep) || steps[0];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[22rem] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100/50 via-transparent to-transparent dark:from-sky-950/30"
        aria-hidden
      />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-36 sm:pb-40">
        <button
          type="button"
          onClick={() => navigate('/vendor')}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-white dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>

        <div className="mt-8 mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400 mb-4">
            <ShieldCheck className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
            Identity
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
            Verify your business
          </h1>
          <p className="mt-3 text-base text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl">
            Complete verification so clients can trust you and unlock the full vendor toolkit.
          </p>
        </div>

        <VendorGuideCallout
          title={VENDOR_PAGE_GUIDES.verification.title}
          steps={VENDOR_PAGE_GUIDES.verification.steps}
          icon={ListChecks}
          className="mb-8"
        />

        {/* Progress */}
        <div className="mb-8 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur px-4 py-4 sm:px-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Step {currentStep} of {steps.length}
                <span className="text-gray-400 dark:text-gray-500 font-normal"> · {activeStep.title}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{activeStep.description}</p>
            </div>
            <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
              {progressPct}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden mb-5">
            <div
              className="h-full rounded-full bg-gray-900 dark:bg-gray-100 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {steps.map((step) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center text-center rounded-xl px-1 py-2.5 sm:py-3 transition-colors ${
                    isActive
                      ? 'bg-gray-900 dark:bg-gray-100'
                      : isCompleted
                        ? 'bg-gray-50 dark:bg-gray-800/80'
                        : 'bg-transparent'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border transition-all ${
                      isCompleted
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : isActive
                          ? 'border-white/20 bg-white/10 text-white dark:border-gray-900/10 dark:bg-gray-900/10 dark:text-gray-900'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                  </div>
                  <p
                    className={`mt-2 text-[10px] sm:text-xs font-medium truncate w-full ${
                      isActive
                        ? 'text-white dark:text-gray-900'
                        : isCompleted
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {step.title}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-8">
          {renderCurrentStep()}
        </div>

        {/* Actions */}
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur-md pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-1 flex items-center justify-between gap-3">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentStep === 1 || isSubmitting}
              className="!rounded-xl min-w-[7rem]"
            >
              Previous
            </Button>

            {currentStep < 4 ? (
              <Button onClick={handleNext} className="!rounded-xl min-w-[7rem]">
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="!rounded-xl inline-flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Submit verification
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AddPlatformModal
        isOpen={showAddPlatformModal}
        newPlatformName={newPlatformName}
        setNewPlatformName={setNewPlatformName}
        onAdd={handleAddPlatform}
        onCancel={handleCancelAddPlatform}
      />
    </div>
  );
};

export default VendorVerification;
